import { useState, useEffect, ChangeEvent, KeyboardEvent, ClipboardEvent } from 'react';
import { Input as InputText } from '../../ui';
import { validationPattern } from '../../../lib/constants'
import { useLeadContext } from '../../../lib/context';

type props = {
    isChangeNo: boolean
}
export function VerifyOtp({ isChangeNo }: props) {
    const [remainingTime, setRemainingTime] = useState(60);
    const { lead, changeNumber, resendOtp, submit, apiError } = useLeadContext();
    const [prevOtp, setPrevOtp] = useState(0);
    const [getValue, setValue] = useState({
        otp1: "",
        otp2: "",
        otp3: "",
        otp4: ""
    });

    useEffect(() => {
        if (remainingTime > 0) {
            setTimeout(() => {
                setRemainingTime(t => t - 1);
            }, 1000);
        }
    }, [remainingTime]);

    // Auto-submit when all fields are filled
    useEffect(() => {
        if (getValue.otp1 && getValue.otp2 && getValue.otp3 && getValue.otp4) {
            const otp = parseInt(`${getValue.otp1}${getValue.otp2}${getValue.otp3}${getValue.otp4}`);
            if (prevOtp !== otp) {
                setPrevOtp(otp);
                submit!(otp);
            }
        }
    }, [getValue, prevOtp, submit]);

    const fillOtpFields = (otpString: string) => {
        if (otpString.length === 4 && /^\d{4}$/.test(otpString)) {
            setValue({
                otp1: otpString[0],
                otp2: otpString[1],
                otp3: otpString[2],
                otp4: otpString[3]
            });
            // Focus the last input after filling
            setTimeout(() => {
                const lastInput = document.querySelector(`input[name=otp4]`) as HTMLInputElement;
                lastInput?.focus();
            }, 0);
        }
    };

    const autoFillOtp = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/\D/g, ''); // Remove non-digits
        if (value.length > 1) {
            fillOtpFields(value.slice(0, 4)); // Take only first 4 digits
        }
    };

    const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const paste = e.clipboardData.getData('text').replace(/\D/g, ''); // Remove non-digits
        fillOtpFields(paste.slice(0, 4)); // Take only first 4 digits
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { name, value, tabIndex } = e.target;
        const numericValue = value.replace(/\D/g, ''); // Remove non-digits
        
        // Handle multi-character input (autofill scenario)
        if (numericValue.length > 1) {
            autoFillOtp(e);
            return;
        }

        // Handle single character input
        if (numericValue.length <= 1) {
            setValue((prevValue) => {
                const newValue = { ...prevValue, [name]: numericValue };
                
                // Move to next field if we entered a digit
                if (numericValue && tabIndex < 4) {
                    setTimeout(() => {
                        const nextInput = document.querySelector(`input[name=otp${tabIndex + 1}]`) as HTMLInputElement;
                        nextInput?.focus();
                    }, 0);
                }
                
                return newValue;
            });
        }
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        const { tabIndex } = e.currentTarget;
        
        if (e.key === 'Backspace') {
            const currentValue = getValue[`otp${tabIndex}` as keyof typeof getValue];
            
            if (!currentValue && tabIndex > 1) {
                // Move to previous field if current is empty
                setTimeout(() => {
                    const prevInput = document.querySelector(`input[name=otp${tabIndex - 1}]`) as HTMLInputElement;
                    prevInput?.focus();
                    prevInput?.setSelectionRange(prevInput.value.length, prevInput.value.length);
                }, 0);
            }
        }
        
        // Allow navigation keys
        if (['ArrowLeft', 'ArrowRight', 'Delete', 'Tab'].includes(e.key)) {
            return;
        }
        
        // Only allow numeric input
        if (!/^\d$/.test(e.key) && e.key !== 'Backspace') {
            e.preventDefault();
        }
    };

    const resend = async () => {
        setValue({
            otp1: "",
            otp2: "",
            otp3: "",
            otp4: ""
        });
        setPrevOtp(0);
        await resendOtp!();
        setRemainingTime(60);
        setTimeout(() => {
            const firstInput = document.querySelector('input[name=otp1]') as HTMLInputElement;
            firstInput?.focus();
        }, 0);
    };

    const verifyOTP = async (e: React.FormEvent) => {
        e.preventDefault();
        if (getValue.otp1 && getValue.otp2 && getValue.otp3 && getValue.otp4) {
            const otp = parseInt(`${getValue.otp1}${getValue.otp2}${getValue.otp3}${getValue.otp4}`);
            if (prevOtp !== otp) {
                setPrevOtp(otp);
                await submit!(otp);
            }
        }
    }
    // useEffect(() => {
    //     if ('OTPCredential' in window) {
    //         const ac = new AbortController();
    //         navigator.credentials.get({
    //             otp: { transport: ['sms'] },
    //             signal: ac.signal
    //         } as CredentialRequestOptions)
    //             .then((credential) => {
    //                 if (credential && 'code' in credential) {
    //                     const otpCredential = credential as any;
    //                     const otpCode = otpCredential.code;
    //                     if (otpCode && otpCode.length === 4) {
    //                         setValue({
    //                             otp1: otpCode[0],
    //                             otp2: otpCode[1],
    //                             otp3: otpCode[2],
    //                             otp4: otpCode[3]
    //                         });

    //                     }
    //                 }
    //             })
    //             .catch((err) => {
    //                 if (err.name !== 'AbortError') {
    //                     console.error('Error retrieving OTP:', err);
    //                 }
    //             });

    //         return () => {
    //             ac.abort();
    //         };
    //     }
    // }, []);
    return (
        <form className="otpBox" onSubmit={verifyOTP}>
            <h4 className="mb24 font26 lineHeight32 textCenter text222 f600">Verify Phone</h4>
            <div className="mainForm">
                <p className="font16 textCenter text222 lineHeight26 mb24 mwidth">
                    We have sent you sms with 4 digit verification code (OTP) on:{' '}
                    <span className="fbold">{"+" + lead?.CountryCode + "-" + lead?.MobileNo}</span>
                </p>
                {isChangeNo && (
                    <p className="font16 mb24" onClick={changeNumber}>
                        <span className="link">Change Number</span>
                    </p>
                )}
                <div className="mb24 form">
                    <input
                        type="text"
                        inputMode="numeric"
                        autoFocus
                        autoComplete="one-time-code"
                        pattern="[0-9]*"
                        maxLength={4}
                        name="otp1"
                        value={getValue.otp1}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        onPaste={handlePaste}
                        tabIndex={1}
                    />
                    <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        pattern="[0-9]*"
                        maxLength={1}
                        name="otp2"
                        value={getValue.otp2}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        onPaste={handlePaste}
                        tabIndex={2}
                    />
                    <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        pattern="[0-9]*"
                        maxLength={1}
                        name="otp3"
                        value={getValue.otp3}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        onPaste={handlePaste}
                        tabIndex={3}
                    />
                    <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        pattern="[0-9]*"
                        maxLength={1}
                        name="otp4"
                        value={getValue.otp4}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        onPaste={handlePaste}
                        tabIndex={4}
                    />
                </div>
                {remainingTime > 0 ? (
                    <p className="font16 mb24">Resend OTP in 00:{remainingTime.toString().padStart(2, '0')}</p>
                ) : null}
                {remainingTime === 0 ? (
                    <p className="font12 text000 mb24">
                        Did not receive the code?{' '}
                        <span className="textpink" onClick={resend}>Resend OTP</span>
                    </p>
                ) : null}
                <button className="primaryBtn" id="confirmButton" type="submit">
                    CONFIRM OTP
                </button>
                <div className="otpErrorBx">
                    {apiError && <div className="otpApiError">{apiError}</div>}
                </div>
            </div>
        </form>
    );
}